/** @file zlib deflate through the platform CompressionStream (workerd, Node, browsers), drained concurrently. */
import { invariant } from '@pose2d/std/invariant';

type ReadResult = { done: boolean; value?: Uint8Array };
type Reader = { read(): Promise<ReadResult> };
type Writer = { write(chunk: Uint8Array): Promise<void>; close(): Promise<void> };
type CompressionStreamCtor = new (format: 'deflate') => { readable: { getReader(): Reader }; writable: { getWriter(): Writer } };

export interface DeflatePipe {
  /** Queues uncompressed bytes; resolves when the stream accepted them (backpressure). */
  write(chunk: Uint8Array): Promise<void>;
  /** Ends the input; resolves after the last compressed chunk reached `onOutput`. */
  close(): Promise<void>;
}

/** Opens a zlib-wrapped deflate stream (the PNG IDAT format); compressed chunks go to `onOutput` in order. */
export function openDeflate(onOutput: (bytes: Uint8Array) => void): DeflatePipe {
  const Ctor = (globalThis as unknown as { CompressionStream?: CompressionStreamCtor }).CompressionStream;
  invariant(Ctor !== undefined, 'CompressionStream is not available on this platform');
  const stream = new Ctor('deflate');
  const writer = stream.writable.getWriter();
  const reader = stream.readable.getReader();
  const drained = drain(reader, onOutput);
  void drained.catch(() => undefined); // the error surfaces from close()
  return {
    write: (chunk) => writer.write(chunk),
    close: async () => {
      await writer.close();
      await drained;
    },
  };
}

/** Reads until the stream ends, handing every non-empty chunk to `onOutput`. */
async function drain(reader: Reader, onOutput: (bytes: Uint8Array) => void): Promise<void> {
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;
    if (value !== undefined && value.length > 0) onOutput(value);
  }
}
